import React, { useState, useEffect, useReducer, useContext, useMemo, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import Pagination from "@material-ui/lab/Pagination";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        overflowX: "hidden",
        ...theme.scrollbarStyles,
        borderTop: "none",
        backgroundColor: "transparent",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    paginationWrapper: {
        padding: "8px 12px",
        borderTop: theme.palette.type === "dark" 
            ? "1px solid rgba(255, 255, 255, 0.08)" 
            : "1px solid rgba(0, 0, 0, 0.08)",
        backgroundColor: theme.palette.type === "dark" ? "#181a20" : "#ffffff",
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
    },
    paginationText: {
        fontSize: "11.5px",
        color: theme.palette.type === "dark" ? "#94a3b8" : "rgb(104, 121, 146)",
        fontWeight: 600,
        marginBottom: "5px",
        letterSpacing: "0.3px",
    },
    pagination: {
        "& .MuiPaginationItem-root": {
            fontSize: "0.8rem",
            fontWeight: 600,
            minWidth: "28px",
            height: "28px",
            padding: 0,
            margin: "0 2px",
            borderRadius: "6px",
            color: theme.palette.type === "dark" ? "#f1f5f9" : "#334155",
            backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
            border: theme.palette.type === "dark" 
                ? "1px solid rgba(255, 255, 255, 0.12)" 
                : "1px solid rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
                backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.14)" : "rgba(0, 0, 0, 0.08)",
                color: theme.palette.type === "dark" ? "#ffffff" : "#0f172a",
            },
        },
        "& .MuiPaginationItem-page.Mui-selected": {
            backgroundColor: (theme.palette.primary.main || "#00e5ff") + " !important",
            color: "#ffffff !important",
            borderColor: "transparent !important",
            fontWeight: "bold",
            boxShadow: theme.palette.type === "dark" 
                ? `0 0 10px ${theme.palette.primary.main}59` 
                : "0 2px 5px rgba(0, 0, 0, 0.2)",
        },
        "& .MuiPaginationItem-icon": {
            color: theme.palette.type === "dark" ? "#f1f5f9" : "#334155",
        },
        "& .Mui-disabled": {
            opacity: "0.3 !important",
        }
    },
}));

const ticketSortAsc = (a, b) => {

    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {

    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

// [PLAN 1089] Smart Merge para preservar datos ricos (whatsapp, user, queue, tags, contact)
// cuando llegan eventos de socket con tickets parciales o despojados
const mergeTicketData = (existingTicket, incomingTicket) => {
    if (!existingTicket) return incomingTicket;
    if (!incomingTicket) return existingTicket;

    return {
        ...existingTicket,
        ...incomingTicket,
        whatsapp: (incomingTicket.whatsapp && incomingTicket.whatsapp.name)
            ? incomingTicket.whatsapp
            : existingTicket.whatsapp,
        user: (incomingTicket.user && incomingTicket.user.name)
            ? incomingTicket.user
            : (incomingTicket.userId && incomingTicket.userId === existingTicket.userId ? existingTicket.user : incomingTicket.user || existingTicket.user),
        queue: (incomingTicket.queue && incomingTicket.queue.name)
            ? incomingTicket.queue
            : (incomingTicket.queueId && incomingTicket.queueId === existingTicket.queueId ? existingTicket.queue : incomingTicket.queue || existingTicket.queue),
        tags: (Array.isArray(incomingTicket.tags) && incomingTicket.tags.length > 0)
            ? incomingTicket.tags
            : existingTicket.tags,
        contact: (incomingTicket.contact && incomingTicket.contact.name)
            ? incomingTicket.contact
            : existingTicket.contact,
    };
};

const reducer = (state, action) => {
    // [ANTIGRAVITY DEBUG]
    // if (action.type === "UPDATE_TICKET" || action.type === "CREATE_TICKET" || (action.type === "LOAD_TICKETS" && false)) {
    //     console.log("[REACT REDUCER] Action=", action.type, " PayloadStatus=", action.payload?.status, " CurrentStatus=", action.status);
    // }
    const sortDir = action.sortDir;

    switch (action.type) {
        case "LOAD_TICKETS": {
            const newTickets = action.payload;
            let newState = [...state];

            newTickets.forEach((ticket) => {
                const ticketIndex = newState.findIndex((t) => t.id === ticket.id);
                if (ticketIndex !== -1) {
                    newState[ticketIndex] = mergeTicketData(newState[ticketIndex], ticket);
                } else {
                    newState.push(ticket);
                }
            });

            if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
                newState.sort(sortDir === 'ASC' ? ticketSortAsc : ticketSortDesc);
            }

            return newState;
        }

        case "RESET_UNREAD": {
            const ticketId = action.payload;
            const newState = state.map((t) =>
                t.id === ticketId ? { ...t, unreadMessages: 0 } : t
            );
            if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
                newState.sort(sortDir === 'ASC' ? ticketSortAsc : ticketSortDesc);
            }
            return newState;
        }

        case "UPDATE_TICKET": {
            const incomingTicket = action.payload;
            const ticketIndex = state.findIndex((t) => t.id === incomingTicket.id);
            const mergedTicket = ticketIndex !== -1
                ? mergeTicketData(state[ticketIndex], incomingTicket)
                : incomingTicket;

            let newState;
            if (ticketIndex !== -1) {
                newState = state.map((t) => (t.id === mergedTicket.id ? mergedTicket : t));
            } else {
                const belongsToStatus = (action.status === "group" && mergedTicket.isGroup && mergedTicket.status !== "closed") || (mergedTicket.status === action.status);
                if (belongsToStatus || action.status === "search" || !action.status) {
                    newState = [mergedTicket, ...state];
                } else {
                    newState = [...state];
                }
            }
            if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
                newState.sort(sortDir === 'ASC' ? ticketSortAsc : ticketSortDesc);
            }
            return newState;
        }

        case "UPDATE_TICKET_UNREAD_MESSAGES": {
            const incomingTicket = action.payload;
            const existingTicket = state.find((t) => t.id === incomingTicket.id);
            const mergedTicket = mergeTicketData(existingTicket, incomingTicket);
            const filteredState = state.filter((t) => t.id !== mergedTicket.id);
            const belongsToStatus = (action.status === "group" && mergedTicket.isGroup && mergedTicket.status !== "closed") || (mergedTicket.status === action.status);
            let newState;
            if (belongsToStatus || action.status === "search" || !action.status) {
                newState = [mergedTicket, ...filteredState];
            } else {
                newState = [...filteredState];
            }
            if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
                newState.sort(sortDir === 'ASC' ? ticketSortAsc : ticketSortDesc);
            }
            return newState;
        }

        case "UPDATE_TICKET_CONTACT": {
            const contact = action.payload;
            return state.map((t) =>
                t.contactId === contact.id ? { ...t, contact } : t
            );
        }

        case "DELETE_TICKET": {
            const ticketId = action.payload;
            const newState = state.filter((t) => t.id !== ticketId);
            if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
                newState.sort(sortDir === 'ASC' ? ticketSortAsc : ticketSortDesc);
            }
            return newState;
        }

        case "RESET":
            return [];

        default:
            return state;
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        withUnreadMessages,
        dateStart,
        dateEnd,
        brandId,
        kanbanBoardId,
        kanbanLaneId,
        laneTagIds
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    const listRef = useRef(null);
    const PAGE_SIZE = 200;

    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    const serializedFilters = useMemo(() => {
        return JSON.stringify({
            status,
            searchParam,
            showAll,
            tags,
            users,
            forceSearch,
            selectedQueueIds,
            whatsappIds,
            statusFilter,
            sortTickets,
            searchOnMessages,
            withUnreadMessages,
            dateStart,
            dateEnd,
            brandId,
            kanbanBoardId,
            kanbanLaneId,
            laneTagIds
        });
    }, [status, searchParam, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages, withUnreadMessages, dateStart, dateEnd, brandId, kanbanBoardId, kanbanLaneId, laneTagIds]);

    const prevFiltersRef = useRef(serializedFilters);
    useEffect(() => {
        if (prevFiltersRef.current !== serializedFilters) {
            prevFiltersRef.current = serializedFilters;
            dispatch({ type: "RESET" });
            setPageNumber(1);
        }
    }, [serializedFilters, dispatch]);

    const { tickets, hasMore, loading, count } = useTickets({
        pageNumber,
        pageSize: PAGE_SIZE,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        withUnreadMessages: withUnreadMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        dateStart,
        dateEnd,
        brandId,
        kanbanBoardId,
        kanbanLaneId,
        laneTagIds,
        forceSearch
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets]);

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            const ticketQueueId = ticket.queueId ? +ticket.queueId : null;
            const ticketUserId = ticket.userId ? +ticket.userId : null;
            const currentUserId = user?.id ? +user.id : null;

            // [ANTIGRAVITY RT FIX] Admin ve TODOS los tickets (igual que la API ListTicketsService línea 218)
            // Si es admin o allUserChat está activado, nunca filtrar por userId
            const isAdminOrAllUsers = profile === "admin" || user?.allUserChat === "enabled";
            if (isAdminOrAllUsers) {
                // Solo verificar filtro de colas si hay colas seleccionadas específicamente
                const selectedQueues = selectedQueueIds.map(q => +q);
                const queueMatch = selectedQueues.length === 0 ||
                    selectedQueues.indexOf(ticketQueueId) > -1 ||
                    (!ticketQueueId && showTicketWithoutQueue);
                return queueMatch;
            }

            // Normalize selectedQueueIds to numbers for comparison
            const selectedQueues = selectedQueueIds.map(q => +q);

            // [ANTIGRAVITY] FIX: Flexibilizar para evitar desapariciones accidentales
            // Si no hay colas seleccionadas, permitir todos (comportamiento de la API)
            const queueMatch = selectedQueues.length === 0 ||
                selectedQueues.indexOf(ticketQueueId) > -1 ||
                (!ticketQueueId && showTicketWithoutQueue);

            return (!ticketUserId || ticketUserId === currentUserId || showAll) && queueMatch;
        }

        const onCompanyTicketTicketsList = (data) => {
            // [ANTIGRAVITY DEBUG]
            // if (data.action === "create" || data.action === "update") {
            //     console.log(`[SOCKET RECEIVE] TicketList received: action=${data.action}, ticketId=${data.ticket?.id}, status=${data.ticket?.status}`);
            // }

            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }

            // [ANTIGRAVITY RT FIX] Manejar action "create" para tickets nuevos en tiempo real
            if (data.action === "create" && data.ticket) {
                const isAllowed = shouldUpdateTicket(data.ticket);
                // console.log(`[SOCKET CREATE] shouldUpdateTicket returned: ${isAllowed} for ticket ${data.ticket.id}`);
                if (isAllowed) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }

            if (data.action === "update") {
                // [ANTIGRAVITY] ESTRATEGIA SEGURA: 
                // Si el ticket YA existe en la lista, actualizarlo SIEMPRE (Ignorar filtros para evitar congelamiento).
                // Si es un ticket NUEVO, aplicar shouldUpdateTicket para respetar privacidad y colas.
                const exists = ticketsList.findIndex(t => t.id === data.ticket.id) !== -1;
                const isAllowed = shouldUpdateTicket(data.ticket);
                // console.log(`[SOCKET UPDATE] Exists=${exists}, shouldUpdateTicket=${isAllowed} for ticket ${data.ticket.id}`);

                if (exists || isAllowed) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET",
                    payload: data?.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if ((data.action === "create" || data.action === "update") && data.ticket) {
                // Aplicar la misma lógica de actualización inteligente
                const exists = ticketsList.findIndex(t => t.id === data.ticket.id) !== -1;

                if (exists || shouldUpdateTicket(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET_UNREAD_MESSAGES",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue, ticketsList]);

    const filteredTickets = useMemo(() => {
        if (status && status !== "search") {
            if (status === "group") {
                return ticketsList.filter(ticket => ticket.isGroup === true && ticket.status !== "closed");
            } else {
                return ticketsList.filter(ticket => ticket.status === status && ticket.isGroup === false);
            }
        }
        if (status === "search") {
            return ticketsList.filter(ticket => ticket.isGroup === false);
        }
        return ticketsList;
    }, [ticketsList, status]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(count !== undefined && count !== null ? count : filteredTickets.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTickets, count]);

    const totalTickets = typeof count === "number" ? count : 0;
    const totalPages = Math.ceil(totalTickets / PAGE_SIZE);

    const handlePageChange = (event, value) => {
        if (value === pageNumber) return;
        dispatch({ type: "RESET" });
        setPageNumber(value);
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    };

    return (
        <Paper elevation={0} className={classes.ticketsListWrapper} style={{ ...style, border: 'none', boxShadow: 'none' }}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                ref={listRef}
            >
                <List style={{ paddingTop: 0 }} >
                    {filteredTickets.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {filteredTickets.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {ticketsList.length > 0 && (totalPages <= 1 || pageNumber === totalPages) && (
                        <div style={{ textAlign: "center", padding: "10px", color: "rgb(104, 121, 146)", fontSize: "12px", borderTop: "1px solid rgba(255,255,255,0.05)", marginTop: 8 }}>
                            {i18n.t("tickets.dateFilter.maxTicketsWarning")}
                        </div>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
            {totalTickets > 0 && (
                <div className={classes.paginationWrapper}>
                    <span className={classes.paginationText}>
                        {`${(pageNumber - 1) * PAGE_SIZE + 1} - ${Math.min(pageNumber * PAGE_SIZE, totalTickets)} de ${totalTickets} tickets`}
                    </span>
                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={pageNumber}
                            onChange={handlePageChange}
                            size="small"
                            color="primary"
                            shape="rounded"
                            siblingCount={0}
                            boundaryCount={1}
                            className={classes.pagination}
                        />
                    )}
                </div>
            )}
        </Paper>
    );
};

export default TicketsListCustom;
